import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  createAuthUser,
  otpFieldHandlers,
  fetchAuthBootstrap,
  loginChooseMethod,
  loginOtp,
  loginStart,
  loginTotp,
  loginTotpEnable,
  loginTotpSetup,
} from './api/auth';
import BrandLogo from './components/BrandLogo/BrandLogo';
import { useTheme } from './theme/ThemeContext';
import './LoginScreen.css';

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M9.9 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.2 3.1M6.1 6.1C3.9 7.7 2 12 2 12s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PasswordField({
  value,
  onChange,
  autoComplete = 'current-password',
  minLength,
  required = true,
  id = 'login-password',
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="login-screen__password-wrap">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        minLength={minLength}
        required={required}
      />
      <button
        type="button"
        className="login-screen__password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

export default function LoginScreen({ onAuthenticated, kickMessage }) {
  const { theme, toggleTheme } = useTheme();
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  // password | choose | enable_totp | totp | otp | bootstrap
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [emailFailed, setEmailFailed] = useState(false);
  const [canUseTotp, setCanUseTotp] = useState(false);
  const [canEnableTotp, setCanEnableTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(kickMessage || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const boot = await fetchAuthBootstrap();
        if (!cancelled && boot.needs_bootstrap) {
          setNeedsBootstrap(true);
          setStep('bootstrap');
        }
      } catch {
        /* API down — still show login */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (kickMessage) setInfo(kickMessage);
  }, [kickMessage]);

  async function onBootstrap(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await createAuthUser({ email, password, is_admin: true });
      setInfo('Admin account created. Sign in next.');
      setNeedsBootstrap(false);
      setStep('password');
      setPassword('');
    } catch (err) {
      setError(err.message || 'Could not create user');
    } finally {
      setBusy(false);
    }
  }

  async function onPassword(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = await loginStart({ email, password });
      setChallengeId(res.challenge_id);
      setEmailFailed(Boolean(res.email_failed));
      setCanUseTotp(Boolean(res.can_use_totp));
      setCanEnableTotp(Boolean(res.can_enable_totp));
      if (res.next === 'choose') {
        setStep('choose');
        setInfo(res.message || 'Choose how to finish sign-in.');
      } else if (res.next === 'totp') {
        setStep('totp');
        setInfo(res.message || 'Enter your authenticator code.');
      } else if (res.session_token) {
        onAuthenticated?.(res);
      } else {
        setStep('otp');
        setInfo(res.message || 'We sent a one-time code to your email.');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  async function pickMethod(method) {
    setBusy(true);
    setError('');
    try {
      const res = await loginChooseMethod({
        challenge_id: challengeId,
        method,
      });
      setEmailFailed(Boolean(res.email_failed));
      if (res.can_use_totp != null) setCanUseTotp(Boolean(res.can_use_totp));
      if (res.next === 'totp') {
        setStep('totp');
        setInfo(res.message || 'Enter the code from your authenticator app.');
      } else {
        setStep('otp');
        setInfo(res.message || 'We sent a one-time code to your email.');
      }
    } catch (err) {
      setError(err.message || 'Could not continue');
    } finally {
      setBusy(false);
    }
  }

  async function startEnableTotp() {
    setBusy(true);
    setError('');
    try {
      const res = await loginTotpSetup({ challenge_id: challengeId });
      setTotpSecret(res.secret || '');
      setTotpUri(res.otpauth_uri || '');
      setTotpCode('');
      setStep('enable_totp');
      setInfo(res.message || 'Add the secret in your authenticator app.');
    } catch (err) {
      setError(err.message || 'Could not start authenticator setup');
    } finally {
      setBusy(false);
    }
  }

  async function onEnableTotp(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await loginTotpEnable({
        challenge_id: challengeId,
        code: totpCode,
        email,
        password,
      });
      if (res.challenge_id) setChallengeId(res.challenge_id);
      setCanUseTotp(true);
      setCanEnableTotp(false);
      setTotpCode('');
      setTotpSecret('');
      setTotpUri('');
      setStep('choose');
      setInfo(res.message || 'Authenticator enabled — choose how to sign in.');
    } catch (err) {
      const msg = err.message || 'Could not enable authenticator';
      setError(msg);
      if (/login expired|challenge expired/i.test(msg)) {
        setStep('password');
        setTotpSecret('');
        setTotpUri('');
        setInfo('Please enter your password again, then enable authenticator.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onTotp(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await loginTotp({ challenge_id: challengeId, code: totpCode });
      if (res.session_token || res.next === 'done') {
        onAuthenticated?.(res);
        return;
      }
      setStep('otp');
      setInfo(res.message || 'Enter the email OTP.');
    } catch (err) {
      setError(err.message || 'Invalid authenticator code');
    } finally {
      setBusy(false);
    }
  }

  async function onOtp(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await loginOtp({ challenge_id: challengeId, code: otpCode });
      onAuthenticated?.(res);
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <button
        type="button"
        className="login-screen__theme"
        onClick={toggleTheme}
        aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        title={theme === 'light' ? 'Dark mode' : 'Light mode'}
      >
        {theme === 'light' ? <MoonIcon /> : <SunIcon />}
      </button>

      <div className="login-screen__card">
        <div className="login-screen__brand-wrap">
          <BrandLogo size="md" />
        </div>
        <p className="login-screen__sub">Dashboard sign in</p>

        {info ? <p className="login-screen__info">{info}</p> : null}
        {error ? <p className="login-screen__error">{error}</p> : null}

        {step === 'bootstrap' && (
          <form className="login-screen__form" onSubmit={onBootstrap}>
            <p className="login-screen__hint">
              No users yet — create the first admin account.
            </p>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <PasswordField
                id="bootstrap-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create admin'}
            </button>
          </form>
        )}

        {step === 'password' && (
          <form className="login-screen__form" onSubmit={onPassword}>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <PasswordField
                id="login-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Continue'}
            </button>
            {needsBootstrap ? (
              <button
                type="button"
                className="login-screen__linkish"
                onClick={() => setStep('bootstrap')}
              >
                Create first admin
              </button>
            ) : null}
          </form>
        )}

        {step === 'choose' && (
          <div className="login-screen__form">
            <p className="login-screen__hint">
              Finish sign-in for <strong>{email}</strong>
            </p>
            {canUseTotp ? (
              <button
                type="button"
                className="login-screen__choice"
                disabled={busy}
                onClick={() => pickMethod('totp')}
              >
                {busy ? 'Please wait…' : 'Sign in with authenticator'}
              </button>
            ) : (
              <button
                type="button"
                className="login-screen__choice"
                disabled={busy || !canEnableTotp}
                onClick={startEnableTotp}
              >
                {busy ? 'Please wait…' : 'Enable authenticator'}
              </button>
            )}
            <button
              type="button"
              className="login-screen__choice login-screen__choice--secondary"
              disabled={busy}
              onClick={() => pickMethod('email')}
            >
              Sign in with email code
            </button>
            <button
              type="button"
              className="login-screen__linkish"
              onClick={() => {
                setStep('password');
                setTotpCode('');
                setOtpCode('');
                setTotpSecret('');
              }}
            >
              Back
            </button>
          </div>
        )}

        {step === 'enable_totp' && (
          <form className="login-screen__form" onSubmit={onEnableTotp}>
            <p className="login-screen__hint">
              Scan this QR with Google Authenticator / Authy, then enter a code to
              enable it.
            </p>
            {totpUri ? (
              <div className="login-screen__qr" aria-label="Authenticator QR code">
                <QRCodeSVG value={totpUri} size={180} bgColor="#ffffff" fgColor="#0b101b" />
              </div>
            ) : null}
            {totpSecret ? (
              <>
                <p className="login-screen__hint">Or enter this key manually:</p>
                <code className="login-screen__secret">{totpSecret}</code>
              </>
            ) : null}
            <label>
              Authenticator code
              <input
                value={totpCode}
                {...otpFieldHandlers(setTotpCode)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Enabling…' : 'Enable & continue'}
            </button>
            <button
              type="button"
              className="login-screen__linkish"
              onClick={() => {
                setStep('choose');
                setTotpCode('');
                setTotpSecret('');
                setTotpUri('');
              }}
            >
              Back
            </button>
          </form>
        )}

        {step === 'totp' && (
          <form className="login-screen__form" onSubmit={onTotp}>
            <p className="login-screen__hint">
              {emailFailed
                ? 'Email unavailable — enter your authenticator code to sign in.'
                : 'Enter the code from your authenticator app.'}
            </p>
            <label>
              Authenticator code
              <input
                value={totpCode}
                {...otpFieldHandlers(setTotpCode)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Sign in with authenticator'}
            </button>
            {!emailFailed ? (
              <button
                type="button"
                className="login-screen__linkish"
                disabled={busy}
                onClick={() => pickMethod('email')}
              >
                Use email code instead
              </button>
            ) : null}
            <button
              type="button"
              className="login-screen__linkish"
              onClick={() => {
                setStep('choose');
                setTotpCode('');
              }}
            >
              Back
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form className="login-screen__form" onSubmit={onOtp}>
            <p className="login-screen__hint">
              Enter the 6-digit code emailed to <strong>{email}</strong>.
            </p>
            <label>
              Email OTP
              <input
                value={otpCode}
                {...otpFieldHandlers(setOtpCode)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Sign in'}
            </button>
            {canUseTotp ? (
              <button
                type="button"
                className="login-screen__linkish"
                disabled={busy}
                onClick={() => {
                  setStep('totp');
                  setOtpCode('');
                  setInfo('Enter your authenticator code to sign in.');
                }}
              >
                Use authenticator instead
              </button>
            ) : canEnableTotp ? (
              <button
                type="button"
                className="login-screen__linkish"
                disabled={busy}
                onClick={startEnableTotp}
              >
                Enable authenticator instead
              </button>
            ) : null}
            <button
              type="button"
              className="login-screen__linkish"
              onClick={() => {
                setStep('choose');
                setOtpCode('');
              }}
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
